import React, { useEffect, useState, useCallback } from 'react';
import { Country } from 'country-state-city';
import { useAuth } from '../../../context/AuthContext';
import { useParams } from 'react-router-dom';
import {
  submitGovernanceAnswers,
  getGovernanceResults,
  getGovernanceQuestions,
  getGovernanceStatus,
  uploadGovernanceDocument,
  getLatestResult,
  getPreviousAnswers,
  saveAnswerVersion,
  uploadAnswerFile,
  listAssessments,
  getAssessmentDetail,
  type PreviousAnswers,
  type LatestResultQuestion,
  type AnswerVersion,
  type DimensionDetail,
  type AssessmentSummary,
} from '../../../api/governanceApi';
import { fetchCompanyProfile, type CompanyProfile } from '../../../api/companyProfileApi';
import { fetchEnterpriseProfile, type EnterpriseProfile } from '../../../api/enterpriseApi';
import {
  getAllRegulations,
  getJurisdictions as getDashboardJurisdictions,
  type Jurisdiction as DashboardJurisdiction,
  type RegulationSummary,
} from '../../../api/dashboardApi';
import {
  GovernanceFinalResult,
} from '../../../hooks/useGovernanceSocket';
import { useGovernance } from '../../../context/GovernanceContext';
import { cached } from '../../../api/apiCache';
import GovernanceVisualizer from '../components/GovernanceVisualizer';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './GovernanceStartPage.module.css';

// ── Constants ───────────────────────────────────────────────────────────────

const AREA_LABELS: Record<string, string> = {
  ownership_accountability: 'Ownership & Accountability',
  board_oversight: 'Board & Leadership Oversight',
  policy_review_process: 'Policy Review Process',
  ai_deployment_approval: 'AI Deployment Approval',
  employee_training: 'Employee Training',
  incident_escalation: 'Incident Escalation',
  third_party_governance: 'Third-Party Governance',
  ai_strategy_roadmap: 'AI Strategy & Roadmap',
  data_minimisation_controls: 'Data Minimisation Controls',
  human_oversight_mechanisms: 'Human Oversight Mechanisms',
};

const STATUS_LABELS: Record<string, string> = {
  met: 'Met', partial: 'Partial', missing: 'Missing',
  non_compliant: 'Non-Compliant', not_applicable: 'N/A',
};

const ALL_COUNTRIES = Country.getAllCountries();
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  usa: 'United States of America', us: 'United States of America',
  uk: 'United Kingdom', uae: 'United Arab Emirates',
};

const SEVERITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
const SCOPE_LABELS: Record<JurisdictionScope, string> = { primary: 'Primary', active: 'Active', upcoming: 'Upcoming', operating: 'Operating' };

type JurisdictionScope = 'primary' | 'active' | 'upcoming' | 'operating';
type CompanyJurisdictionOption = { value: string; label: string; scope: JurisdictionScope };

// ── Jurisdiction helpers (unchanged) ────────────────────────────────────────

function normalizeJurisdiction(v: string | null | undefined): string { return (v ?? '').toLowerCase().replace(/[–—,-]/g, ' ').replace(/\s+/g, ' ').trim(); }
function formatJurisdictionDisplay(v: string | null | undefined): string { const r = v?.trim(); if (!r) return ''; const p = r.split(/\s*[–—-]\s*|\s*,\s*/).map(s => s.trim()).filter(Boolean); if (!p.length) return r; return p.filter((s, i) => p.findIndex(c => normalizeJurisdiction(c) === normalizeJurisdiction(s)) === i).join(', '); }

function pushJurisdictionOption(opts: CompanyJurisdictionOption[], seen: Set<string>, v: string | null | undefined, scope: JurisdictionScope) {
  const cv = formatJurisdictionDisplay(v); if (!cv) return; const k = normalizeJurisdiction(cv); if (!k || seen.has(k)) return;
  const prefix = scope === 'primary' ? 'Primary' : scope === 'active' ? 'Active' : scope === 'upcoming' ? 'Upcoming' : 'Operating';
  seen.add(k); opts.push({ value: cv, label: `${prefix} · ${cv}`, scope });
}

function buildJurisdictionOptions(ep: EnterpriseProfile | null, cp: CompanyProfile | null): CompanyJurisdictionOption[] {
  const opts: CompanyJurisdictionOption[] = []; const seen = new Set<string>();
  pushJurisdictionOption(opts, seen, ep?.jurisdiction, 'primary');
  ep?.other_jurisdictions?.forEach(v => pushJurisdictionOption(opts, seen, v, 'active'));
  ep?.new_jurisdictions?.forEach(v => pushJurisdictionOption(opts, seen, v, 'upcoming'));
  cp?.operating_jurisdictions?.forEach(e => {
    const same = normalizeJurisdiction(e.country) === normalizeJurisdiction(e.state ?? '');
    pushJurisdictionOption(opts, seen, same ? e.country : [e.state, e.country].filter(Boolean).join(', '), 'operating');
  });
  return opts;
}

function matchesDashboardJurisdiction(j: DashboardJurisdiction, sel: string): boolean {
  const t = normalizeJurisdiction(sel), n = normalizeJurisdiction(j.name), c = normalizeJurisdiction(j.country_name);
  const parts = formatJurisdictionDisplay(sel).split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const ncp = parts.filter(p => !isKnownCountry(p)).map(p => normalizeJurisdiction(p));
  const comb = normalizeJurisdiction([j.name, j.country_name].filter(Boolean).join(' '));
  const rcomb = normalizeJurisdiction([j.country_name, j.name].filter(Boolean).join(' '));
  if (ncp.length > 0) return ncp.some(p => n === p || comb === p || rcomb === p || n.startsWith(`${p} `) || p.startsWith(`${n} `));
  if (n === t || c === t || comb === t || rcomb === t) return true;
  if (n.startsWith(`${t} `) || c.startsWith(`${t} `) || t.startsWith(`${n} `) || t.startsWith(`${c} `)) return true;
  return false;
}

function getJurisdictionCodeCandidates(js: DashboardJurisdiction[]): string[] { return Array.from(new Set(js.map(j => j.code?.trim()).filter((c): c is string => !!c))); }

async function searchJurisdictionCandidates(sel: string): Promise<DashboardJurisdiction[]> {
  const f = formatJurisdictionDisplay(sel); const parts = f.split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const terms = Array.from(new Set([f, ...parts, ...parts.filter(p => !isKnownCountry(p)), parts[0] || '', parts.length > 1 ? parts[parts.length - 1] : ''].filter(Boolean)));
  const results = await Promise.all(terms.map(t => cached(`juris:${t}`, 300_000, () => getDashboardJurisdictions({ q: t, limit: 100 }))));
  const m = new Map<string, DashboardJurisdiction>(); results.flat().forEach(j => m.set(j.id, j)); return Array.from(m.values());
}

function isKnownCountry(v: string | null | undefined): boolean { const n = normalizeJurisdiction(v); if (!n) return false; if (COUNTRY_NAME_ALIASES[n]) return true; return ALL_COUNTRIES.some(c => normalizeJurisdiction(c.name) === n || normalizeJurisdiction(c.isoCode) === n); }
function resolveCountryName(v: string | null | undefined): string { const f = formatJurisdictionDisplay(v); const n = normalizeJurisdiction(f); if (!n) return ''; if (COUNTRY_NAME_ALIASES[n]) return COUNTRY_NAME_ALIASES[n]; const m = ALL_COUNTRIES.find(c => normalizeJurisdiction(c.name) === n || normalizeJurisdiction(c.isoCode) === n); return m?.name || f; }

function deriveCountryFallback(sel: string, js: DashboardJurisdiction[]): string {
  const ns = normalizeJurisdiction(sel);
  const rc = js.map(j => j.country_name).find(cn => { const nc = normalizeJurisdiction(cn); return !!nc && ns.includes(nc); });
  if (rc) return formatJurisdictionDisplay(rc);
  const parts = formatJurisdictionDisplay(sel).split(/\s*,\s*/).map(s => s.trim()).filter(Boolean);
  const dc = parts.find(p => isKnownCountry(p)) || [...parts].reverse().find(p => isKnownCountry(p));
  if (dc) return resolveCountryName(dc);
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

async function loadArtifactsForJurisdictionCodes(codes: string[]): Promise<RegulationSummary[]> {
  if (!codes.length) return [];
  const res = await Promise.all(codes.slice(0, 5).map(c => cached(`regs:${c}`, 300_000, () => getAllRegulations({ jurisdiction_code: c, limit: 200, sort_by: 'date', sort_order: 'desc' }))));
  const m = new Map<string, RegulationSummary>(); res.forEach(r => r.regulations.forEach(reg => m.set(String(reg.id), reg))); return Array.from(m.values());
}

function formatPolicyType(v?: string): string { const c = v?.trim(); if (!c) return 'General'; return c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase()); }
function maturityLabel(s: number): string { if (s >= 90) return 'Optimised'; if (s >= 76) return 'Managed'; if (s >= 61) return 'Defined'; if (s >= 41) return 'Developing'; return 'Initial'; }

// ── Donut Chart Component ────────────────────────────────────────────────────

function DonutChart({ met, partial, missing }: { met: number; partial: number; missing: number }) {
  const total = met + partial + missing;
  const r = 58;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * r;
  const cx = 80;
  const cy = 80;

  // Segments: Met, Partial, Missing. Empty state = full grey.
  const metFrac = total > 0 ? met / total : 0;
  const partialFrac = total > 0 ? partial / total : 0;
  const missingFrac = total > 0 ? missing / total : 0;

  const metDash = metFrac * circumference;
  const partialDash = partialFrac * circumference;
  const missingDash = missingFrac * circumference;

  // Gaps between segments (2px visual gap)
  const gap = total > 0 ? 2 : 0;

  // Offsets: start from top (-90deg = -circumference/4)
  const metOffset = -circumference / 4 + gap / 2;
  const partialOffset = metOffset - metDash + gap;
  const missingOffset = partialOffset - partialDash + gap;

  return (
    <div className={styles.donutWrapper}>
      <svg width="160" height="160" viewBox="0 0 160 160" style={{ overflow: 'visible' }}>
        {/* Background track */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke="var(--ds-surface-2)"
          strokeWidth={strokeWidth}
        />
        {total > 0 && (
          <>
            {/* Met segment */}
            {met > 0 && (
              <circle
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke="var(--ds-success)"
                strokeWidth={strokeWidth}
                strokeDasharray={`${Math.max(metDash - gap, 0)} ${circumference}`}
                strokeDashoffset={metOffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 0.6s ease' }}
              />
            )}
            {/* Partial segment */}
            {partial > 0 && (
              <circle
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke="var(--ds-warning)"
                strokeWidth={strokeWidth}
                strokeDasharray={`${Math.max(partialDash - gap, 0)} ${circumference}`}
                strokeDashoffset={partialOffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 0.6s ease' }}
              />
            )}
            {/* Missing segment */}
            {missing > 0 && (
              <circle
                cx={cx} cy={cy} r={r}
                fill="none"
                stroke="var(--ds-danger)"
                strokeWidth={strokeWidth}
                strokeDasharray={`${Math.max(missingDash - gap, 0)} ${circumference}`}
                strokeDashoffset={missingOffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dasharray 0.6s ease' }}
              />
            )}
          </>
        )}
        {/* Center text */}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="22" fontWeight="700" fill="#101828">{total}</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize="11" fill="var(--ds-text-faint)">total</text>
      </svg>
      <div className={styles.donutLegend}>
        <div className={styles.donutLegendItem}>
          <span className={styles.donutLegendDot} style={{ background: 'var(--ds-success)' }} />
          <span className={styles.donutLegendLabel}>Met</span>
          <span className={styles.donutLegendCount}>{met}</span>
        </div>
        <div className={styles.donutLegendItem}>
          <span className={styles.donutLegendDot} style={{ background: 'var(--ds-warning)' }} />
          <span className={styles.donutLegendLabel}>Partial</span>
          <span className={styles.donutLegendCount}>{partial}</span>
        </div>
        <div className={styles.donutLegendItem}>
          <span className={styles.donutLegendDot} style={{ background: 'var(--ds-danger)' }} />
          <span className={styles.donutLegendLabel}>Missing</span>
          <span className={styles.donutLegendCount}>{missing}</span>
        </div>
      </div>
    </div>
  );
}

// ── Page Component ──────────────────────────────────────────────────────────

export default function GovernanceStartPage() {
  const { user, token } = useAuth();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const { state: wsState, start, resume, reset, setQuestions, setFinalResult } = useGovernance();

  // Modes: 'history' (version list), 'dashboard' (show results), 'start' (run new analysis)
  const [mode, setMode] = useState<'loading' | 'history' | 'dashboard' | 'start'>('loading');
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);

  // Stored result from DB
  const [storedResult, setStoredResult] = useState<GovernanceFinalResult | null>(null);
  const [storedMeta, setStoredMeta] = useState<{ assessment_id?: string; version: number; jurisdiction: string | null; assessed_at: string | null } | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [storedQuestions, setStoredQuestions] = useState<LatestResultQuestion[]>([]);
  const [dimensionDetails, setDimensionDetails] = useState<Record<string, DimensionDetail>>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Previous answers for auto-fill
  const [previousAnswers, setPreviousAnswers] = useState<PreviousAnswers | null>(null);

  // Input state
  const [policyIds, setPolicyIds] = useState<string[]>([]);
  const [inputError, setInputError] = useState<string | null>(null);
  const [matchedArtifacts, setMatchedArtifacts] = useState<RegulationSummary[]>([]);
  const [jurisdictionOptions, setJurisdictionOptions] = useState<CompanyJurisdictionOption[]>([]);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('');
  const [setupLoading, setSetupLoading] = useState(true);
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [artifactSourceNote, setArtifactSourceNote] = useState<string | null>(null);
  const [showAllPolicies, setShowAllPolicies] = useState(false);
  const [inputStep, setInputStep] = useState<'jurisdiction' | 'start'>('jurisdiction');

  // HITL
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, { file: File; uploading: boolean; uploaded: boolean; filename: string }[]>>({});
  const [submittingAnswers, setSubmittingAnswers] = useState(false);

  // Dashboard sub-tabs
  const [activeTab, setActiveTab] = useState<'overview' | 'breakdown' | 'gaps' | 'questionnaire'>('overview');
  const [breakdownFilter, setBreakdownFilter] = useState({ area: '', status: '' });
  const [expandedGaps, setExpandedGaps] = useState<Set<string>>(new Set());

  // Questionnaire editing state
  const [editingQuestion, setEditingQuestion] = useState<number | null>(null); // question row id
  const [editText, setEditText] = useState('');
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState<Set<number>>(new Set());

  const companyId = user?.id?.toString() || '';

  // WS state
  const isIdle = wsState.stage === 'idle';
  const isError = wsState.stage === 'error';
  const isDone = wsState.stage === 'done';
  const isLoading = !isIdle && !isError && !isDone && wsState.stage !== 'awaiting_hitl';
  const isHitl = wsState.stage === 'awaiting_hitl';
  const liveResult = wsState.finalResult;

  // ── On mount: fetch assessment history ────────────────────────────
  useEffect(() => {
    if (urlSessionId) return; // URL override handled separately
    (async () => {
      try {
        const list = await listAssessments();
        setAssessments(list);
        if (list.length > 0) {
          setMode('history');
        } else {
          setMode('start');
        }
      } catch {
        setMode('start');
      }
    })();
  }, [urlSessionId]);

  const loadAssessmentVersion = useCallback(async (assessmentId: string) => {
    setAssessmentsLoading(true);
    try {
      const data = await getAssessmentDetail(assessmentId);
      if (data.final_result) {
        setStoredResult(data.final_result as unknown as GovernanceFinalResult);
        setStoredMeta({ assessment_id: data.assessment_id, version: data.version, jurisdiction: data.jurisdiction, assessed_at: data.assessed_at });
        setStoredQuestions(data.questions || []);
        setDimensionDetails(data.dimension_details || {});
        setMode('dashboard');
      }
    } catch { /* ignore */ }
    finally { setAssessmentsLoading(false); }
  }, []);

  // ── When live analysis finishes, store and switch to dashboard ─────
  useEffect(() => {
    if (isDone && wsState.sessionId && !liveResult) {
      const timer = setTimeout(async () => {
        try {
          const result = await getGovernanceResults(wsState.sessionId);
          if ('overall_score' in result) {
            setFinalResult(result as unknown as GovernanceFinalResult);
          }
        } catch { /* ignore */ }
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isDone, wsState.sessionId, liveResult, setFinalResult]);

  // When we have a live result, switch to dashboard mode and fetch enriched details from DB
  useEffect(() => {
    if (liveResult) {
      setStoredResult(liveResult);
      setStoredMeta(null); // will show "Latest" instead of version
      setMode('dashboard');
      // Fetch dimension_details, questions, and assessment_id from DB
      getLatestResult().then(data => {
        setDimensionDetails(data.dimension_details || {});
        setStoredQuestions(data.questions || []);
        setStoredMeta({ assessment_id: data.assessment_id, version: data.version, jurisdiction: data.jurisdiction, assessed_at: data.assessed_at });
      }).catch(() => {});
      // Refresh the assessments list so history view is up-to-date
      listAssessments().then(setAssessments).catch(() => {});
    }
  }, [liveResult]);

  // ── URL session load ──────────────────────────────────────────────
  useEffect(() => {
    if (!urlSessionId || !token) return;
    (async () => {
      try {
        const statusRes = await getGovernanceStatus(urlSessionId);
        if (statusRes.status === 'complete') {
          const result = await getGovernanceResults(urlSessionId);
          if ('overall_score' in result) {
            setFinalResult(result as unknown as GovernanceFinalResult);
          }
        } else if (statusRes.status === 'awaiting_hitl') {
          setMode('start');
          const qs = await getGovernanceQuestions(urlSessionId);
          setQuestions(qs.map(q => ({ question_id: q.question_id, dimension_id: q.dimension_id ?? '', policy_id: q.policy_id, question_text: q.question_text, question_type: q.question_type ?? null, document_type: q.document_type ?? null, document_description: q.document_description ?? null, resolves_question_ids: q.resolves_question_ids ?? null, resolves_dimension_ids: q.resolves_dimension_ids ?? null })));
        }
      } catch { setMode('start'); }
    })();
  }, [urlSessionId, token, setFinalResult, setQuestions]);

  // ── Visibility-based fallback ─────────────────────────────────────
  useEffect(() => {
    const sid = wsState.sessionId;
    if (!sid || liveResult) return;
    const h = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const s = await getGovernanceStatus(sid);
        if (s.status === 'complete') { const r = await getGovernanceResults(sid); if ('overall_score' in r) setFinalResult(r as unknown as GovernanceFinalResult); }
      } catch { /* ignore */ }
    };
    document.addEventListener('visibilitychange', h);
    return () => document.removeEventListener('visibilitychange', h);
  }, [wsState.sessionId, liveResult, setFinalResult]);

  // ── Load jurisdictions ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSetupLoading(true);
      try {
        const [er, cr] = await Promise.allSettled([cached('enterprise-profile', 120_000, fetchEnterpriseProfile), cached('company-profile', 120_000, fetchCompanyProfile)]);
        if (cancelled) return;
        const ep = er.status === 'fulfilled' ? er.value : null;
        const cp = cr.status === 'fulfilled' ? cr.value : null;
        const opts = buildJurisdictionOptions(ep, cp);
        const fb = opts[0]?.value || formatJurisdictionDisplay(ep?.jurisdiction) || formatJurisdictionDisplay(ep?.other_jurisdictions?.[0]) || formatJurisdictionDisplay(ep?.new_jurisdictions?.[0]) || formatJurisdictionDisplay(cp?.operating_jurisdictions?.[0] ? [cp.operating_jurisdictions[0].state, cp.operating_jurisdictions[0].country].filter(Boolean).join(', ') : '');
        if (!opts.length && fb) opts.push({ value: fb, label: `Primary · ${fb}`, scope: 'primary' });
        setJurisdictionOptions(opts);
        setSelectedJurisdiction(prev => prev || fb || '');
        if (!opts.length) setInputError('No company jurisdictions found yet.');
      } catch { if (!cancelled) setInputError('Could not load company jurisdictions.'); }
      finally { if (!cancelled) setSetupLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Load artifacts when jurisdiction changes ──────────────────────
  useEffect(() => {
    let cancelled = false;
    if (setupLoading || !selectedJurisdiction) { setMatchedArtifacts([]); setPolicyIds([]); setArtifactSourceNote(null); return; }
    (async () => {
      setArtifactLoading(true); setArtifactSourceNote(null);
      try {
        const jr = await searchJurisdictionCandidates(selectedJurisdiction); if (cancelled) return;
        const mj = jr.filter(j => matchesDashboardJurisdiction(j, selectedJurisdiction));
        let arts = await loadArtifactsForJurisdictionCodes(getJurisdictionCodeCandidates(mj));
        const fc = deriveCountryFallback(selectedJurisdiction, jr);
        const useFb = !arts.length && !!fc && normalizeJurisdiction(fc) !== normalizeJurisdiction(selectedJurisdiction);
        if (useFb) {
          const cr = await cached(`juris-country:${fc}`, 300_000, () => getDashboardJurisdictions({ q: fc, limit: 100 }));
          if (cancelled) return;
          const cm = cr.filter(j => matchesDashboardJurisdiction(j, fc) || normalizeJurisdiction(j.country_name) === normalizeJurisdiction(fc));
          arts = await loadArtifactsForJurisdictionCodes(getJurisdictionCodeCandidates(cm));
          if (arts.length) setArtifactSourceNote(`No exact dataset for ${selectedJurisdiction}. Using ${fc}.`);
        }
        if (cancelled) return;
        setMatchedArtifacts(arts); setPolicyIds(arts.map(a => String(a.id)));
        if (!arts.length) setInputError(`No regulation artifacts found for ${selectedJurisdiction}.`); else setInputError(null);
      } catch { if (!cancelled) { setMatchedArtifacts([]); setPolicyIds([]); setInputError('Could not load artifacts.'); } }
      finally { if (!cancelled) setArtifactLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedJurisdiction, setupLoading]);

  useEffect(() => { setShowAllPolicies(false); setInputStep('jurisdiction'); }, [selectedJurisdiction]);

  // ── Fetch questions + auto-supply on HITL ─────────────────────────
  useEffect(() => {
    if (isHitl && wsState.sessionId && wsState.questions.length === 0) {
      (async () => {
        try {
          const qs = await getGovernanceQuestions(wsState.sessionId);
          setQuestions(qs.map(q => ({ question_id: q.question_id, dimension_id: q.dimension_id ?? '', policy_id: q.policy_id, question_text: q.question_text, question_type: q.question_type ?? null, document_type: q.document_type ?? null, document_description: q.document_description ?? null, resolves_question_ids: q.resolves_question_ids ?? null, resolves_dimension_ids: q.resolves_dimension_ids ?? null })));
          if (!previousAnswers) {
            try {
              const prev = await getPreviousAnswers(); setPreviousAnswers(prev);
              if (prev.answers.length > 0) {
                const af: Record<string, string> = {};
                for (const pa of prev.answers) { if (pa.answer_text) { const mq = qs.find(q => q.question_text === pa.question_text); if (mq) af[mq.question_id] = pa.answer_text; } }
                if (Object.keys(af).length > 0) setAnswers(p => ({ ...af, ...p }));
              }
            } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      })();
    }
  }, [isHitl, wsState.sessionId, wsState.questions.length, setQuestions, previousAnswers]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handleRerun = () => {
    reset();
    setMode('start');
    setActiveTab('overview');
    setAnswers({});
    setUploadedFiles({});
    if (!previousAnswers) getPreviousAnswers().then(setPreviousAnswers).catch(() => {});
  };

  const handleDownloadPdf = async () => {
    const aid = storedMeta?.assessment_id;
    if (!aid || !token) return;
    setDownloadingPdf(true);
    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
      const res = await fetch(`${apiBase}/api/v1/governance/assessments/${aid}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`PDF download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `governance-report-v${storedMeta?.version || 1}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
    finally { setDownloadingPdf(false); }
  };

  const handleStart = () => {
    if (!selectedJurisdiction) { setInputError('Select a jurisdiction.'); return; }
    if (!policyIds.length) { setInputError('No policies available.'); return; }
    setInputError(null);
    start(companyId, policyIds, token!, selectedJurisdiction);
  };

  const handleFileSelect = (qid: string, file: File) => {
    setUploadedFiles(prev => ({
      ...prev,
      [qid]: [...(prev[qid] || []), { file, uploading: false, uploaded: false, filename: file.name }],
    }));
  };

  const handleSubmitAnswers = async () => {
    setSubmittingAnswers(true);
    try {
      // Upload all files for each question
      for (const q of wsState.questions) {
        const files = uploadedFiles[q.question_id] || [];
        for (let i = 0; i < files.length; i++) {
          if (!files[i].uploaded) {
            setUploadedFiles(p => {
              const arr = [...(p[q.question_id] || [])];
              arr[i] = { ...arr[i], uploading: true };
              return { ...p, [q.question_id]: arr };
            });
            try {
              await uploadGovernanceDocument(wsState.sessionId, q.question_id, files[i].file);
              setUploadedFiles(p => {
                const arr = [...(p[q.question_id] || [])];
                arr[i] = { ...arr[i], uploading: false, uploaded: true };
                return { ...p, [q.question_id]: arr };
              });
            } catch {
              setUploadedFiles(p => {
                const arr = [...(p[q.question_id] || [])];
                arr[i] = { ...arr[i], uploading: false };
                return { ...p, [q.question_id]: arr };
              });
            }
          }
        }
      }
      // Build answer text with file references
      const all = wsState.questions.map(q => {
        const files = uploadedFiles[q.question_id] || [];
        const txt = answers[q.question_id] || '';
        const fileRefs = files.filter(f => f.uploaded).map(f => `[Document uploaded: ${f.filename}]`).join('\n');
        return { question_id: q.question_id, answer_text: [fileRefs, txt].filter(Boolean).join('\n') || '' };
      });
      if (all.length > 0) await submitGovernanceAnswers(wsState.sessionId, all);
      resume(wsState.sessionId, token!);
    } catch { /* ignore */ }
    finally { setSubmittingAnswers(false); }
  };

  const toggleGap = (id: string) => { setExpandedGaps(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleRow = (id: string) => { setExpandedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleHistory = (qRowId: number) => { setExpandedHistory(prev => { const n = new Set(prev); n.has(qRowId) ? n.delete(qRowId) : n.add(qRowId); return n; }); };

  const handleSaveAnswer = async (qRowId: number) => {
    setSavingAnswer(true);
    try {
      const newVer = await saveAnswerVersion(qRowId, editText || null);
      // Upload files to this version
      for (const f of editFiles) {
        await uploadAnswerFile(qRowId, newVer.version_id, f);
      }
      // Refresh stored questions
      const data = await getLatestResult();
      setStoredQuestions(data.questions || []);
      setEditingQuestion(null);
      setEditText('');
      setEditFiles([]);
    } catch { /* ignore */ }
    finally { setSavingAnswer(false); }
  };
  const selectedOption = jurisdictionOptions.find(o => o.value === selectedJurisdiction);
  const visibleArtifacts = showAllPolicies ? matchedArtifacts : matchedArtifacts.slice(0, 3);

  const togglePolicy = (id: string) => {
    setPolicyIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  // What to display
  const fr = storedResult; // the result to render in dashboard

  // ── Render ────────────────────────────────────────────────────────

  if (mode === 'loading') {
    return <div className={styles.page}><div className={styles.container}><p className={styles.inputNote} style={{ padding: '60px 0', textAlign: 'center' }}>Loading governance results...</p></div></div>;
  }

  // ═══════════════════════════════════════════════════════════════════
  //  MODE: HISTORY — list all assessment versions
  // ═══════════════════════════════════════════════════════════════════
  if (mode === 'history') {
    const completedAssessments = assessments.filter(a => a.status === 'complete');
    const runningAssessments = assessments.filter(a => a.status !== 'complete');
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.historyHeader}>
            <div>
              <Eyebrow tone="default" dot>GOVERNANCE</Eyebrow>
              <PageHeading
                accentWord="History"
                accentTone="orange"
                size="h2"
                align="left"
                level="h1"
                className={styles.title}
              >
                Assessment History
              </PageHeading>
              <p className={styles.subtitle}>All governance assessments for your organisation. Click any row to view the full results.</p>
            </div>
            <button className={styles.startBtn} style={{ width: 'auto', padding: '10px 24px' }} onClick={() => { reset(); setMode('start'); }}>
              New Analysis
            </button>
          </div>

          {assessmentsLoading && (
            <p className={styles.inputNote} style={{ textAlign: 'center', padding: '20px 0' }}>Loading assessment...</p>
          )}

          {completedAssessments.length === 0 && !assessmentsLoading && (
            <div className={styles.historyEmpty}>
              <p>No completed assessments yet.</p>
              <button className={styles.startBtn} style={{ width: 'auto', padding: '10px 24px', marginTop: 12 }} onClick={() => { reset(); setMode('start'); }}>
                Start your first assessment
              </button>
            </div>
          )}

          {completedAssessments.length > 0 && (
            <table className={styles.historyTable}>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Jurisdiction</th>
                  <th>Score</th>
                  <th>Health</th>
                  <th>Dimensions</th>
                  <th>Coverage</th>
                  <th>Assessed</th>
                </tr>
              </thead>
              <tbody>
                {completedAssessments.map(a => {
                  const score = a.overall_score != null ? Math.round(a.overall_score) : '-';
                  const coverage = a.coverage_score != null ? `${Math.round(a.coverage_score * 100)}%` : '-';
                  const dims = [
                    a.dimensions_met != null ? `${a.dimensions_met} met` : null,
                    a.dimensions_partial != null ? `${a.dimensions_partial} partial` : null,
                    a.dimensions_missing != null ? `${a.dimensions_missing} missing` : null,
                  ].filter(Boolean).join(', ') || (a.total_dimensions ? `${a.total_dimensions} total` : '-');
                  const dateStr = a.assessed_at ? new Date(a.assessed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

                  return (
                    <tr
                      key={a.assessment_id || a.session_id}
                      className={styles.historyRow}
                      onClick={() => a.assessment_id && loadAssessmentVersion(a.assessment_id)}
                    >
                      <td><span className={styles.historyVersion}>v{a.version ?? '?'}</span></td>
                      <td>{a.jurisdiction || '-'}</td>
                      <td>
                        <span className={styles.historyScore} data-rating={a.health_rating}>
                          {score}
                        </span>
                      </td>
                      <td>
                        <span className={styles.completeBadge} data-rating={a.health_rating} style={{ padding: '4px 12px', fontSize: 11 }}>
                          {a.health_rating || '-'}
                        </span>
                      </td>
                      <td className={styles.historyDims}>{dims}</td>
                      <td>{coverage}</td>
                      <td className={styles.historyDate}>{dateStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {runningAssessments.length > 0 && (
            <div className={styles.historyRunning}>
              <h3>In progress</h3>
              {runningAssessments.map(a => (
                <div key={a.session_id} className={styles.historyRunningRow}>
                  <span className={styles.historyVersion}>v{a.version ?? '?'}</span>
                  <span>{a.jurisdiction || 'Unknown jurisdiction'}</span>
                  <span className={styles.historyRunningStatus}>{a.current_node ? `At: ${a.current_node.replace(/_/g, ' ')}` : a.status}</span>
                  <span className={styles.historyDate}>{a.created_at ? new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  MODE: DASHBOARD — show stored results
  // ═══════════════════════════════════════════════════════════════════
  if (mode === 'dashboard' && fr) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={`${styles.dashboard} ${styles.dashboardVisible}`}>
            {/* Back to history */}
            {assessments.length > 0 && (
              <button className={styles.historyBackBtn} onClick={() => setMode('history')}>
                &larr; All assessments
              </button>
            )}

            {/* Header */}
            <div className={styles.dashboardHeader}>
              <div>
                <Eyebrow tone="default" dot>GOVERNANCE</Eyebrow>
                <PageHeading
                  accentWord="Assessment"
                  accentTone="orange"
                  size="h2"
                  align="left"
                  level="h1"
                  className={styles.title}
                >
                  Governance Assessment
                </PageHeading>
                <p className={styles.dashboardMeta}>
                  {storedMeta?.version ? `Version ${storedMeta.version} · ` : ''}
                  {fr.assessed_at ? `Completed ${new Date(fr.assessed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}
                  {storedMeta?.jurisdiction ? ` · ${storedMeta.jurisdiction}` : ''}
                </p>
              </div>
              <div className={styles.dashboardActions}>
                <span className={styles.completeBadge} data-rating={fr.health_rating}>Assessment complete</span>
                {storedMeta?.assessment_id && (
                  <button
                    className={styles.btnSecondary}
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    style={{ width: 'auto', padding: '8px 18px' }}
                  >
                    {downloadingPdf ? 'Generating...' : 'Download PDF'}
                  </button>
                )}
                <button className={styles.startBtn} onClick={handleRerun} style={{ width: 'auto', padding: '8px 18px', fontSize: 13 }}>New Analysis</button>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className={styles.tabBar}>
              {(['overview', 'breakdown', 'gaps', 'questionnaire'] as const).map(tab => (
                <button key={tab} className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`} onClick={() => setActiveTab(tab)}>
                  {tab === 'overview' ? 'Overview' : tab === 'breakdown' ? 'Policy breakdown' : tab === 'gaps' ? 'Gaps & recommendations' : 'Questionnaire'}
                </button>
              ))}
            </div>

            {/* ── Overview ─────────────────────────────────────── */}
            {activeTab === 'overview' && (
              <div className={styles.tabContent}>
                <div className={styles.metricsRow}>
                  <div className={styles.metricCard}>
                    <div className={styles.metricInfoBtn}>
                      ℹ
                      <span className={styles.metricTooltip}>Weighted compliance score (0–100) across all governance areas</span>
                    </div>
                    <div className={styles.metricValue} data-rating={fr.health_rating}>{fr.overall_score}</div>
                    <div className={styles.metricLabel}>Overall Score</div>
                  </div>
                  <div className={styles.metricCard}>
                    <div className={styles.metricInfoBtn}>
                      ℹ
                      <span className={styles.metricTooltip}>Percentage of requirements with at least partial evidence</span>
                    </div>
                    <div className={styles.metricValue}>{Math.round(fr.coverage_score * 100)}%</div>
                    <div className={styles.metricLabel}>Coverage</div>
                  </div>
                  <div className={styles.metricCard}>
                    <div className={styles.metricInfoBtn}>
                      ℹ
                      <span className={styles.metricTooltip}>Score for the ownership &amp; accountability governance area</span>
                    </div>
                    <div className={styles.metricValue}>{Math.round(fr.ownership_score * 100)}%</div>
                    <div className={styles.metricLabel}>Ownership</div>
                  </div>
                  <div className={styles.metricCard}>
                    <div className={styles.metricInfoBtn}>
                      ℹ
                      <span className={styles.metricTooltip}>Qualitative maturity level based on overall score</span>
                    </div>
                    <span className={styles.maturityBadge}>{maturityLabel(fr.overall_score)}</span>
                    <div className={styles.metricLabel}>Maturity</div>
                  </div>
                </div>
                <div className={styles.twoCol}>
                  <div className={styles.areaChart}>
                    <h3>Governance area coverage</h3>
                    {Object.entries(fr.area_scores).map(([area, data]) => {
                      const s = Math.round(data.score * 100);
                      const c = s >= 75 ? 'var(--ds-success)' : s >= 50 ? 'var(--ds-warning)' : s <= 0 ? 'var(--ds-border-strong)' : 'var(--ds-danger)';
                      const showLabelInside = s > 15;
                      return (
                        <div key={area} className={styles.areaBar}>
                          <span className={styles.areaLabel}>{AREA_LABELS[area] || area}</span>
                          <div className={styles.areaTrack} style={{ height: 12 }}>
                            <div className={styles.areaFill} style={{ width: `${Math.max(s, 2)}%`, background: c, height: '100%', position: 'relative' }}>
                              {showLabelInside && (
                                <span className={styles.areaFillLabel}>{s}%</span>
                              )}
                            </div>
                          </div>
                          {!showLabelInside && <span className={styles.areaValue} style={{ color: c }}>{s > 0 ? `${s}%` : 'N/A'}</span>}
                          {showLabelInside && <span className={styles.areaValue} style={{ color: 'var(--ds-text-faint)', fontSize: 11 }}>&nbsp;</span>}
                        </div>
                      );
                    })}
                  </div>
                  <div className={styles.summaryCol}>
                    <div className={styles.statusSummaryCard}>
                      <h3>Status summary</h3>
                      <DonutChart
                        met={fr.elements_met}
                        partial={fr.elements_partial}
                        missing={fr.elements_missing}
                      />
                    </div>
                  </div>
                </div>
                {fr.merge_stats && (<div className={styles.twoCol}><div className={styles.statusSummaryCard}><h3>Dimension reduction funnel</h3><div className={styles.funnelSteps}><div className={styles.funnelStep}><span className={styles.funnelValue}>{fr.merge_stats.raw_count}</span><span className={styles.funnelLabel}>Raw</span></div><span className={styles.funnelArrow}>&rarr;</span><div className={styles.funnelStep}><span className={styles.funnelValue}>{fr.merge_stats.post_dedup_count}</span><span className={styles.funnelLabel}>Deduped</span></div>{fr.merge_stats.post_filter_count != null && (<><span className={styles.funnelArrow}>&rarr;</span><div className={styles.funnelStep}><span className={styles.funnelValue}>{fr.merge_stats.post_filter_count}</span><span className={styles.funnelLabel}>Filtered</span></div></>)}</div></div>
                  {fr.deferred_summary && fr.deferred_summary.total > 0 && (<div className={styles.statusSummaryCard}><h3>Deferred dimensions</h3><p className={styles.inputNote}>{fr.deferred_summary.total} low-priority not assessed.</p><div className={styles.deferredList}>{Object.entries(fr.deferred_summary.by_area).map(([a, c]) => (<div key={a} className={styles.summaryRow}><span>{AREA_LABELS[a] || a}</span><strong>{c}</strong></div>))}</div></div>)}
                </div>)}
                {fr.policy_coverage && Object.keys(fr.policy_coverage).length > 0 && (<div className={styles.statusSummaryCard}><h3>Policy coverage</h3><table className={styles.breakdownTable}><thead><tr><th>Policy</th><th>Raw</th><th>Covered</th><th>Met</th><th>Partial</th><th>Missing</th></tr></thead><tbody>{Object.entries(fr.policy_coverage).map(([pid, cov]) => (<tr key={pid}><td className={styles.breakdownReq}>{pid.length > 12 ? `${pid.slice(0, 12)}...` : pid}</td><td>{cov.raw_dimensions}</td><td>{cov.covered}</td><td>{cov.met}</td><td>{cov.partial}</td><td>{cov.missing}</td></tr>))}</tbody></table></div>)}
                {fr.critical_gaps.length > 0 && (<div className={styles.criticalGapsCard}><h3>Critical gaps</h3>{fr.critical_gaps.map((g, i) => (<div key={g.dimension_id || i} className={styles.gapRow}><button className={styles.gapRowHeader} onClick={() => toggleGap(g.dimension_id)}><span className={styles.gapDot} data-severity="critical" /><span className={styles.gapTitle}>{g.question}</span><span className={styles.severityBadge} data-sev="critical">Critical</span><span className={styles.areaBadge}>{AREA_LABELS[g.area] || g.area}</span></button>{expandedGaps.has(g.dimension_id) && (<div className={styles.gapBody}>{g.gap && <p className={styles.gapDesc}>{g.gap}</p>}{g.evidence_used?.length > 0 && <div className={styles.gapEvidence}><strong>Evidence:</strong><ul>{g.evidence_used.map((e, j) => <li key={j}>{e}</li>)}</ul></div>}{g.recommendation && <p className={styles.gapRec}>{g.recommendation}</p>}</div>)}</div>))}</div>)}
              </div>
            )}

            {/* ── Breakdown ────────────────────────────────────── */}
            {activeTab === 'breakdown' && (
              <div className={styles.tabContent}>
                <div className={styles.breakdownFilters}>
                  <select value={breakdownFilter.area} onChange={e => setBreakdownFilter(p => ({ ...p, area: e.target.value }))}><option value="">All areas</option>{Object.entries(AREA_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                  <select value={breakdownFilter.status} onChange={e => setBreakdownFilter(p => ({ ...p, status: e.target.value }))}><option value="">All statuses</option>{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                </div>
                <div className={styles.breakdownCount}>{fr.dimension_results.filter(r => { if (breakdownFilter.area && r.area !== breakdownFilter.area) return false; if (breakdownFilter.status && r.status !== breakdownFilter.status) return false; return true; }).length} of {fr.dimension_results.length} dimensions</div>
                <table className={styles.breakdownTable}>
                  <thead><tr><th>Requirement</th><th>Area</th><th>Status</th><th>Severity</th><th>Score</th></tr></thead>
                  <tbody>
                    {fr.dimension_results.filter(r => {
                      if (breakdownFilter.area && r.area !== breakdownFilter.area) return false;
                      if (breakdownFilter.status && r.status !== breakdownFilter.status) return false;
                      return true;
                    }).map(r => {
                      const em = fr.evidence_map?.[r.dimension_id];
                      const detail = dimensionDetails[r.dimension_id];
                      const isExpanded = expandedRows.has(r.dimension_id);
                      // Merge: DB detail takes precedence, fall back to data already in dimension_result
                      const policyReference = detail?.policy_reference ?? r.policy_reference;
                      const reasoning = detail?.reasoning ?? r.reasoning;
                      const companyEvidence = detail?.company_evidence_summary ?? r.company_evidence_summary;
                      const gapText = detail?.gap ?? r.gap;
                      const recommendationText = detail?.recommendation ?? r.recommendation;
                      const evidenceUsed = detail?.evidence_used ?? (r.evidence_used?.length ? r.evidence_used : undefined);
                      const docSources = detail?.doc_sources ?? r.doc_sources;
                      const sourcePolicyName = detail?.source_policy_name ?? r.source_policy_name;
                      return (
                        <React.Fragment key={r.dimension_id}>
                          <tr
                            className={`${styles.breakdownRow} ${isExpanded ? styles.breakdownRowSelected : ''}`}
                            onClick={() => toggleRow(r.dimension_id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td className={styles.breakdownReq}>{em?.question || r.question || r.dimension_id}</td>
                            <td><span className={styles.areaBadge}>{AREA_LABELS[r.area ?? ''] || r.area}</span></td>
                            <td><span className={styles.statusBadge} data-status={r.status}>{STATUS_LABELS[r.status] || r.status}</span></td>
                            <td>{r.severity ? <span className={styles.severityBadge} data-sev={r.severity}>{r.severity}</span> : '-'}</td>
                            <td>{((r.score_contribution ?? 0) * 100).toFixed(0)}%</td>
                          </tr>
                          <tr className={styles.breakdownDetailTr}>
                            <td colSpan={5}>
                              <div className={`${styles.breakdownDetailPanel} ${isExpanded ? styles.open : ''}`}>
                                <div className={styles.breakdownDetailCard}>
                                  <button
                                    className={styles.breakdownDetailClose}
                                    onClick={(e) => { e.stopPropagation(); toggleRow(r.dimension_id); }}
                                  >
                                    ✕ Close
                                  </button>
                                  <div className={styles.breakdownDetailTitle}>
                                    {em?.question || detail?.question || r.question || r.dimension_id}
                                  </div>
                                  <div className={styles.breakdownDetailBadges}>
                                    <span className={styles.statusBadge} data-status={r.status}>{STATUS_LABELS[r.status] || r.status}</span>
                                    {r.severity && <span className={styles.severityBadge} data-sev={r.severity}>{r.severity}</span>}
                                    <span className={styles.areaBadge}>{AREA_LABELS[r.area ?? ''] || r.area}</span>
                                    <span style={{ fontSize: 12, color: 'var(--ds-text-muted)' }}>Score: {((r.score_contribution ?? 0) * 100).toFixed(0)}%</span>
                                  </div>

                                  {sourcePolicyName && (
                                    <div className={styles.breakdownDetailSection}>
                                      <strong>Regulation:</strong> {sourcePolicyName}
                                    </div>
                                  )}

                                  {policyReference && (
                                    <div className={styles.breakdownDetailSection}>
                                      <strong>Clause / Article:</strong> {policyReference}
                                    </div>
                                  )}

                                  {reasoning && (
                                    <div className={styles.breakdownDetailSection}>
                                      <strong>Reasoning:</strong> {reasoning}
                                    </div>
                                  )}

                                  {companyEvidence && (
                                    <div className={styles.breakdownDetailSection}>
                                      <strong>Company Evidence:</strong> {companyEvidence}
                                    </div>
                                  )}

                                  {gapText && (
                                    <div className={`${styles.breakdownDetailSection} ${styles.gap}`}>
                                      <strong>Gap:</strong> {gapText}
                                    </div>
                                  )}

                                  {recommendationText && (
                                    <div className={`${styles.breakdownDetailSection} ${styles.rec}`}>
                                      <strong>Recommendation:</strong> {recommendationText}
                                    </div>
                                  )}

                                  {evidenceUsed && evidenceUsed.length > 0 && (
                                    <div className={styles.breakdownDetailSection}>
                                      <strong>Evidence Used:</strong>
                                      <ul className={styles.breakdownDetailEvidenceList}>
                                        {evidenceUsed.map((e, i) => <li key={i}>{e}</li>)}
                                      </ul>
                                    </div>
                                  )}

                                  {docSources && docSources.length > 0 && (
                                    <div className={styles.breakdownDetailSection}>
                                      <strong>Source Docs:</strong> {docSources.join(', ')}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Gaps ─────────────────────────────────────────── */}
            {activeTab === 'gaps' && (
              <div className={styles.tabContent}>
                <div className={styles.gapsTwoCol}>
                  <div className={styles.gapsLeftCol}>
                    <h3 className={styles.gapsLeftColHeader}>All gaps</h3>
                    {[...fr.critical_gaps, ...fr.high_gaps, ...fr.dimension_results.filter(r => r.severity && r.severity !== 'critical' && r.severity !== 'high' && r.status !== 'met')]
                      .sort((a, b) => (SEVERITY_ORDER[a.severity ?? 'low'] ?? 3) - (SEVERITY_ORDER[b.severity ?? 'low'] ?? 3))
                      .map((g, i) => (
                        <div key={g.dimension_id || i} className={styles.gapCard}>
                          <div className={styles.gapCardHeader}>
                            <span className={styles.gapDot} data-severity={g.severity} />
                            <span className={styles.gapCardTitle}>{(g as any).question || fr.evidence_map?.[g.dimension_id]?.question || g.dimension_id}</span>
                          </div>
                          {g.gap && <p className={styles.gapCardDesc}>{g.gap}</p>}
                          <div className={styles.gapCardBadges}>
                            {g.severity && <span className={styles.severityBadge} data-sev={g.severity}>{g.severity}</span>}
                            {(g as any).area && <span className={styles.areaBadge}>{AREA_LABELS[(g as any).area] || (g as any).area}</span>}
                          </div>
                        </div>
                      ))}
                    {fr.critical_gaps.length === 0 && fr.high_gaps.length === 0 && (
                      <p className={styles.noGaps}>No critical or high-severity gaps found.</p>
                    )}
                  </div>
                  <div className={styles.recsColSticky}>
                    <div className={styles.recsCard}>
                      <h3>Top recommendations</h3>
                      <ol className={styles.recsList}>
                        {fr.top_recommendations.map((r, i) => (
                          <li key={i} className={styles.recItem}>
                            <p>{r.recommendation}</p>
                            {r.severity && <span className={styles.severityBadge} data-sev={r.severity}>{r.severity}</span>}
                          </li>
                        ))}
                      </ol>
                      {fr.top_recommendations.length === 0 && <p className={styles.noGaps}>All requirements met.</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Questionnaire (editable, versioned) ──────────── */}
            {activeTab === 'questionnaire' && (
              <div className={styles.tabContent}>
                {fr.resolution_stats && (<div className={styles.resolutionSummary}><div className={styles.resStatItem}><span className={styles.resStatValue}>{fr.resolution_stats.resolved_by_existing_docs}</span><span className={styles.resStatLabel}>Auto-resolved</span></div><div className={styles.resStatItem}><span className={styles.resStatValue}>{fr.resolution_stats.document_requests_sent}</span><span className={styles.resStatLabel}>Doc uploads</span></div><div className={styles.resStatItem}><span className={styles.resStatValue}>{fr.resolution_stats.standalone_questions_sent}</span><span className={styles.resStatLabel}>Questions</span></div><div className={styles.resStatItem}><span className={styles.resStatValue}>{fr.resolution_stats.resolved_by_text_answers}</span><span className={styles.resStatLabel}>Text answers</span></div></div>)}

                {storedQuestions.length > 0 ? (
                  storedQuestions.map(q => {
                    const dimResult = fr.dimension_results.find(r => r.dimension_id === q.dimension_id);
                    const latest = q.latest_answer;
                    const isEditing = editingQuestion === q.id;
                    const showHistory = expandedHistory.has(q.id);

                    return (
                      <div key={q.question_id} className={styles.qaCard}>
                        <div className={styles.qaCardHeader}>
                          <span className={styles.qaTypeBadge}>{q.question_type === 'document_request' ? 'Document' : 'Question'}</span>
                          <span className={styles.qaTypeBadge} style={{ marginLeft: 4 }}>#{q.question_id.slice(0, 8)}</span>
                          {latest && <span className={styles.qaTypeBadge} style={{ marginLeft: 4 }}>v{latest.version}</span>}
                        </div>

                        <div className={styles.qaQuestion}><strong>Q:</strong> {q.question_text}</div>

                        {/* Latest answer display */}
                        {!isEditing && (
                          <div className={styles.qaAnswer}>
                            <strong>A:</strong>{' '}
                            {latest ? (
                              <>
                                {latest.answer_text || ''}
                                {latest.files.length > 0 && (
                                  <div style={{ marginTop: 4 }}>
                                    {latest.files.map(f => (
                                      <div key={f.id} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>
                                        {f.file_name || f.file_s3_key}
                                        {f.file_size_bytes && <span style={{ color: 'var(--ds-text-faint)', marginLeft: 6 }}>({Math.round(f.file_size_bytes / 1024)}KB)</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {!latest.answer_text && latest.files.length === 0 && <em>No answer provided</em>}
                              </>
                            ) : (
                              <em>No answer provided</em>
                            )}
                          </div>
                        )}

                        {/* Edit mode */}
                        {isEditing && (
                          <div style={{ marginTop: 8 }}>
                            <textarea
                              className={styles.docAltText}
                              value={editText}
                              onChange={e => setEditText(e.target.value)}
                              placeholder="Enter updated answer..."
                              style={{ minHeight: 80 }}
                            />
                            <div className={styles.docUploadActions} style={{ marginTop: 8 }}>
                              <label className={styles.fileInputLabel} style={{ background: 'var(--ds-text-muted)' }}>
                                <input type="file" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx" className={styles.fileInputHidden} multiple
                                  onChange={e => { if (e.target.files) setEditFiles(prev => [...prev, ...Array.from(e.target.files!)]); }} />
                                Attach files
                              </label>
                              {editFiles.length > 0 && <span className={styles.inputNote}>{editFiles.length} new file{editFiles.length !== 1 ? 's' : ''}</span>}
                            </div>
                            {editFiles.map((f, i) => (
                              <div key={i} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>{f.name}</div>
                            ))}
                            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                              <button className={styles.startBtn} style={{ width: 'auto', padding: '8px 18px', fontSize: 13 }} onClick={() => handleSaveAnswer(q.id)} disabled={savingAnswer}>
                                {savingAnswer ? 'Saving...' : 'Save new version'}
                              </button>
                              <button className={styles.btnSecondary} style={{ width: 'auto', padding: '8px 14px' }} onClick={() => { setEditingQuestion(null); setEditText(''); setEditFiles([]); }}>
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Actions row */}
                        {!isEditing && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button
                              className={styles.viewAllBtn}
                              onClick={() => { setEditingQuestion(q.id); setEditText(latest?.answer_text || ''); setEditFiles([]); }}
                            >
                              Edit answer
                            </button>
                            {q.answer_versions.length > 1 && (
                              <button className={styles.viewAllBtn} onClick={() => toggleHistory(q.id)}>
                                {showHistory ? 'Hide history' : `${q.answer_versions.length} versions`}
                              </button>
                            )}
                          </div>
                        )}

                        {/* Version history */}
                        {showHistory && !isEditing && q.answer_versions.length > 1 && (
                          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--gray-100)' }}>
                            {q.answer_versions.map(v => (
                              <div key={v.version_id} style={{ marginBottom: 10, padding: '8px 12px', background: v.version === latest?.version ? 'var(--primary-50)' : 'var(--ds-surface-2)', borderRadius: 'var(--ds-radius-md)', border: '1px solid var(--ds-border)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ds-link)' }}>v{v.version}</span>
                                  <span style={{ fontSize: 11, color: 'var(--ds-text-faint)' }}>{v.created_at ? new Date(v.created_at).toLocaleString() : ''}</span>
                                </div>
                                {v.answer_text && <p style={{ fontSize: 13, color: 'var(--ds-text-secondary)', margin: '0 0 4px', lineHeight: 1.5 }}>{v.answer_text}</p>}
                                {v.files.map(f => (
                                  <div key={f.id} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>{f.file_name || f.file_s3_key}</div>
                                ))}
                                {!v.answer_text && v.files.length === 0 && <em style={{ fontSize: 12, color: 'var(--ds-text-faint)' }}>Empty answer</em>}
                              </div>
                            ))}
                          </div>
                        )}

                        {dimResult && (
                          <div className={styles.qaOutcomeBadge}>
                            <span style={{ color: 'var(--ds-text-faint)', fontWeight: 400 }}>Resolved as</span>
                            <span className={styles.statusBadge} data-status={dimResult.status}>{STATUS_LABELS[dimResult.status] || dimResult.status}</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className={styles.noGaps}>No questionnaire was needed for this assessment.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  MODE: START — jurisdiction picker + running + HITL
  // ═══════════════════════════════════════════════════════════════════
  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Idle: show input form ─────────────────────────── */}
        {isIdle && !liveResult && (
          <>
            <div className={styles.inputHeader}>
              <span className={styles.headerEyebrow}>Governance workflow</span>
              <p className={styles.subtitle}>Select a jurisdiction, then start the assessment.</p>
              <div className={styles.flowSteps}>
                <button type="button" className={`${styles.flowStep} ${inputStep === 'jurisdiction' ? styles.flowStepActive : ''}`} onClick={() => setInputStep('jurisdiction')}>
                  <span className={styles.flowStepNumber}>1</span><div className={styles.flowStepCopy}><strong>Select jurisdiction</strong><span>Choose the region to evaluate.</span></div>
                </button>
                <button type="button" className={`${styles.flowStep} ${inputStep === 'start' ? styles.flowStepActive : ''} ${!selectedJurisdiction ? styles.flowStepDisabled : ''}`} onClick={() => { if (selectedJurisdiction) setInputStep('start'); }} disabled={!selectedJurisdiction}>
                  <span className={styles.flowStepNumber}>2</span><div className={styles.flowStepCopy}><strong>Start assessment</strong><span>Review and run.</span></div>
                </button>
              </div>
            </div>

            <div className={styles.inputCard}>
              {inputStep === 'jurisdiction' ? (
                <div className={styles.inputMain}>
                  <div className={styles.selectorBlock}>
                    <span className={styles.sectionEyebrow}>Step 1</span>
                    <h2 className={styles.sectionTitle}>Select Jurisdiction</h2>
                    <p className={styles.sectionText}>This determines which policies are recommended.</p>
                    <label htmlFor="governance-jurisdiction" className={styles.fieldLabel}>Company jurisdiction</label>
                    <select id="governance-jurisdiction" className={styles.jurisdictionSelect} value={selectedJurisdiction} onChange={e => setSelectedJurisdiction(e.target.value)} disabled={setupLoading}>
                      <option value="">{setupLoading ? 'Loading...' : 'Select a jurisdiction'}</option>
                      {jurisdictionOptions.map(o => <option key={`${o.scope}-${o.value}`} value={o.value}>{o.label}</option>)}
                    </select>
                    {selectedJurisdiction && selectedOption && (<div className={styles.selectedJurisdictionCard}><div><span className={styles.selectedJurisdictionLabel}>Selected</span><div className={styles.selectedJurisdictionValue}>{selectedJurisdiction}</div></div><span className={`${styles.scopeBadge} ${styles[`scope_${selectedOption.scope}`]}`}>{SCOPE_LABELS[selectedOption.scope]}</span></div>)}
                    <p className={styles.inputNote}>Choose a jurisdiction and matching policies will load.</p>
                  </div>
                  <div className={styles.autoArtifactsBox}>
                    <div className={styles.autoArtifactsHeader}><div><span className={styles.autoArtifactsEyebrow}>Preview</span><span className={styles.autoArtifactsTitle}>Recommended policies</span></div><span className={styles.autoArtifactsCount}>{policyIds.length} selected</span></div>
                    {artifactSourceNote && <p className={styles.fallbackNotice}>{artifactSourceNote}</p>}
                    {artifactLoading ? <p className={styles.inputNote}>Loading policies...</p> : matchedArtifacts.length > 0 ? (
                      <><div className={styles.policyList}>{visibleArtifacts.map(a => { const selected = policyIds.includes(String(a.id)); return (<div key={a.id} className={`${styles.policyItem} ${!selected ? styles.policyItemDeselected : ''}`} onClick={() => togglePolicy(String(a.id))} role="checkbox" aria-checked={selected} tabIndex={0} onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); togglePolicy(String(a.id)); } }}><span className={`${styles.policyCheckbox} ${selected ? styles.policyCheckboxChecked : ''}`}>{selected && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>}</span><div><div className={styles.policyItemTitle}>{a.title}</div><div className={styles.policyItemMeta}><span className={styles.policyTypeBadge}>{formatPolicyType(a.regulation_type || a.content_type)}</span><span>{a.jurisdiction_name || selectedJurisdiction}</span><span className={styles.policyId}>#{a.id}</span></div></div></div>); })}</div>
                      {matchedArtifacts.length > 3 && <button type="button" className={styles.viewAllBtn} onClick={() => setShowAllPolicies(p => !p)}>{showAllPolicies ? 'Show fewer' : `View all ${matchedArtifacts.length}`}</button>}
                      <div className={styles.continueRow}><button type="button" className={styles.startBtn} onClick={() => setInputStep('start')}>Continue to Start Assessment</button></div></>
                    ) : <p className={styles.inputNote}>{selectedJurisdiction ? 'No policies found.' : 'Select a jurisdiction first.'}</p>}
                  </div>
                </div>
              ) : (
                <div className={styles.summaryPanel}><div className={styles.summaryCard}>
                  <span className={styles.summaryEyebrow}>Step 2</span><h3 className={styles.summaryTitle}>Start Assessment</h3>
                  <div className={styles.summaryList}><div className={styles.summaryRow}><span>Jurisdiction</span><strong>{selectedJurisdiction}</strong></div><div className={styles.summaryRow}><span>Policies</span><strong>{policyIds.length}</strong></div></div>
                  {(inputError || wsState.error) && <p className={styles.errorMsg}>{inputError || wsState.error}</p>}
                  <div className={styles.startRow}>
                    <button type="button" className={styles.btnSecondary} onClick={() => setInputStep('jurisdiction')}>Back</button>
                    {storedResult && <button type="button" className={styles.btnSecondary} onClick={() => setMode('dashboard')}>Cancel</button>}
                    <button className={styles.startBtn} onClick={handleStart} disabled={!selectedJurisdiction || !policyIds.length || setupLoading || artifactLoading}>Start Assessment ({policyIds.length} policies)</button>
                  </div>
                </div></div>
              )}
            </div>
          </>
        )}

        {/* ── Running / HITL ─────────────────────────────────── */}
        {(isLoading || isHitl || isError) && !liveResult && (
          <div className={styles.loadingWrapper}>
            <GovernanceVisualizer stage={wsState.stage} stageMessage={wsState.stageMessage} stepperNodes={wsState.stepperNodes} areasProcessed={wsState.areasProcessed} dimensionsExtracted={wsState.dimensionsExtracted} comparisonProgress={wsState.comparisonProgress} questionsCount={wsState.questionsCount} eventLog={wsState.eventLog} nodeStats={wsState.nodeStats} />

            {isHitl && wsState.questions.length > 0 && (
              <div className={styles.hitlSection}>
                <div className={styles.hitlBanner}>{wsState.questions.length} item{wsState.questions.length !== 1 ? 's' : ''} need your input</div>

                {wsState.questions.filter(q => q.question_type === 'document_request').length > 0 && (
                  <div className={styles.hitlGroup}><h3 className={styles.hitlGroupTitle}>Document uploads</h3><p className={styles.hitlGroupDesc}>Upload multiple files or type — both accepted.</p>
                    {wsState.questions.filter(q => q.question_type === 'document_request').map(q => { const files = uploadedFiles[q.question_id] || []; return (
                      <div key={q.question_id} className={styles.docUploadCard}>
                        <div className={styles.docUploadHeader}><span className={styles.docTypeBadge}>{q.document_type || 'Document'}</span>{q.resolves_question_ids && <span className={styles.docResolveCount}>Resolves {q.resolves_question_ids.length}</span>}</div>
                        <p className={styles.docUploadPrompt}>{q.question_text}</p>
                        {q.document_description && <p className={styles.docUploadDesc}>{q.document_description}</p>}
                        <div className={styles.docUploadActions}>
                          <label className={styles.fileInputLabel}><input type="file" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx" className={styles.fileInputHidden} onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(q.question_id, f); }} />Add file</label>
                          <span className={styles.inputNote}>{files.length} file{files.length !== 1 ? 's' : ''} attached</span>
                        </div>
                        {files.map((fe, i) => (
                          <div key={i} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>
                            {fe.uploaded ? '\u2713 ' : fe.uploading ? '\u25CB ' : ''}{fe.filename}
                          </div>
                        ))}
                        <textarea className={styles.docAltText} value={answers[q.question_id] || ''} onChange={e => setAnswers(p => ({ ...p, [q.question_id]: e.target.value }))} placeholder="Or type an answer..." />
                      </div>); })}
                  </div>)}

                {wsState.questions.filter(q => q.question_type !== 'document_request').length > 0 && (
                  <div className={styles.hitlGroup}><h3 className={styles.hitlGroupTitle}>Questions</h3><p className={styles.hitlGroupDesc}>Text or file — both accepted.</p>
                    {wsState.questions.filter(q => q.question_type !== 'document_request').map(q => { const files = uploadedFiles[q.question_id] || []; return (
                      <div key={q.question_id} className={styles.docUploadCard}>
                        <p className={styles.docUploadPrompt}>{q.question_text}</p>
                        <textarea className={styles.docAltText} value={answers[q.question_id] || ''} onChange={e => setAnswers(p => ({ ...p, [q.question_id]: e.target.value }))} placeholder="Enter answer..." style={{ minHeight: 60 }} />
                        <div className={styles.docUploadActions} style={{ marginTop: 8 }}>
                          <label className={styles.fileInputLabel} style={{ background: 'var(--ds-text-muted)' }}><input type="file" accept=".pdf,.doc,.docx,.txt,.md,.csv,.xlsx" className={styles.fileInputHidden} onChange={e => { const f = e.target.files?.[0]; if (f) handleFileSelect(q.question_id, f); }} />Attach file</label>
                          {files.length > 0 && <span className={styles.inputNote}>{files.length} file{files.length !== 1 ? 's' : ''}</span>}
                        </div>
                        {files.map((fe, i) => (
                          <div key={i} className={styles.fileSelectedName} style={{ marginBottom: 2 }}>
                            {fe.uploaded ? '\u2713 ' : fe.uploading ? '\u25CB ' : ''}{fe.filename}
                          </div>
                        ))}
                      </div>); })}
                  </div>)}

                <div className={styles.submitRow}><button className={styles.startBtn} onClick={handleSubmitAnswers} disabled={submittingAnswers}>{submittingAnswers ? 'Submitting...' : 'Submit all responses'}</button></div>
              </div>
            )}

            {isError && (<div className={styles.errorBanner}><p>{wsState.error}</p><button className={styles.startBtn} onClick={reset}>Retry</button></div>)}
          </div>
        )}
      </div>
    </div>
  );
}